import {
    Component,
    EventEmitter,
    Input,
    Output,
    OnInit,
    OnChanges,
    Injector
} from '@angular/core';

import { LocationService } from './location.service';
import { CanLoadMixin, mixinLoadable } from '@common/mixins/loadable.mixin';
import { applyMixins, Base } from '@common/mixins';
import { LoggingService } from '@services/logging.service';

const Mixins: CanLoadMixin & typeof Base = applyMixins(mixinLoadable)
@Component({
    selector: 'location-chooser',
    templateUrl: './location-chooser.component.html',
    styles: [`
      .add-child-control {
        padding: 2px;
      }
    `]
})
export class LocationChooserComponent extends Mixins implements OnInit, OnChanges {
    @Input() location: any;

    // TODO (kevin.stone): This should not be necessary
    //      but don't have time to figure out the correct interface.
    // Optional pass in key to automatically load LocationPosition if it is not in memory
    @Input() locationKey: number;

    @Input() readonly: boolean;
    @Input() required: boolean;
    @Output() locationChange: EventEmitter<any> = new EventEmitter<any>();
    @Output() rootLocationChange: EventEmitter<any> = new EventEmitter<any>();

    // cache all location queries for 1 minute
    readonly msToCacheData = 60 * 1000;
    readonly COMPONENT_LOG_TAG = 'location-chooser';

    // state
    ancestors: any[];
    locationList: any[];
    childCount: number;

    constructor(
        injector: Injector,
        private locationService: LocationService,
        private loggingService: LoggingService
    ) {
        super(injector)
    }

    async ngOnInit() {
        super.ngOnInit();
        // default required to true
        if (this.required !== false) {
            this.required = true;
        }
        await this.initializeLocation();
    }

    async ngOnChanges(changes: any) {
        if (changes.location) {
            if (this.location) {
                await this.initializeLocation();
            } else {
                this.ancestors = [];
                this.locationList = [];
                this.childCount = 0;
                await this.initializeLocation();
            }
        }
    }

    async initializeLocation(): Promise<void> {
        try {
            this.setLoading(true);
            const locationPositionKey = this.location ?
                this.location.C_LocationPosition_key : this.locationKey;

            if (!locationPositionKey) {
                return await this.getDataForNullLocation();
            }

            return await this.getAllLocationData(locationPositionKey);
        } catch (ex) {
            this.loggingService.logError("Error loading initializing location data", ex, this.COMPONENT_LOG_TAG, false);
        } finally {
            this.setLoading(false);
        }

    }

    /**
     * Handles selection of a location from the ancestor list.
     */
    selectAncestor(selectedLocation: any): void {
        this.location = selectedLocation;

        this.locationChange.emit(this.location);
    }

    /**
     * Handles selection of a location from the select list.
     */
    selectLocation(selectedLocation: any): void {
        if (this.ancestors.length === 0) {
            this.rootLocationChange.emit(selectedLocation);
        }
        this.locationChange.emit(this.location);
    }

    /**
     * Adds a child level beneath the current location.
     */
    addLevel(): void {
        const currentKey = this.location.C_LocationPosition_key;

        this.getChildren(currentKey).then((children: any[]) => {
            if (children.length > 0) {
                this.ancestors.push(this.location);

                // Set new location to first child of current location
                this.location = children[0];

                this.locationChange.emit(this.location);
            }
        });
    }

    /**
     * Gets all data for a location; sets member variables.
     */
    private async getAllLocationData(locationKey: number): Promise<void> {
        await this.getAncestors(locationKey);
        await this.getChildCount(locationKey);
        return await this.getLocationList();
    }

    /**
     * Only loads root locations for workgroup
     *   to fill selector
     */
    private getDataForNullLocation(): Promise<void> {
        return this.getLocationList();
    }

    /**
     * Gets the ancestors of a location.
     */
    private async getAncestors(locationKey: number): Promise<void> {
        const data = await this.locationService.getParents(locationKey, this.msToCacheData);
        this.ancestors = data;
    }

    /**
     * Gets the child count for a location.
     */
    private async getChildCount(locationKey: number): Promise<void> {
        const count = await this.locationService.getChildPositionsCount(locationKey, true);
        this.childCount = count;
    }

    /**
     * Gets the location list containing the children of the current location's parent
     * (i.e., siblings of the current).
     */
    private async getLocationList(): Promise<void> {
        let parentLocationKey: number = null;

        const ancestorCount: number = this.ancestors.length;
        if (ancestorCount > 0) {
            parentLocationKey = this.ancestors[ancestorCount - 1].C_LocationPosition_key;
        }

        const data = await this.getChildren(parentLocationKey);
        this.locationList = data.filter((item: any) => {
            // only include active locations in list unless it's the current location
            return item.IsActive === true || item.C_LocationPosition_key === this.location?.C_LocationPosition_key;
        });
    }

    private getChildren(parentLocationKey: number): Promise<any[]> {
        return this.locationService.getChildPositionsWithoutChildEntities(
            parentLocationKey,
        );
    }
}
